import { inject, Injectable } from '@angular/core';
import {
  NotificationServiceInterface,
  NotificationParams,
  NotificationSeverity,
  ERROR_TOAST_DEFAULTS,
  ERROR_TOAST_STYLE_CLASS,
  BUSINESS_VALIDATION_DEFAULTS,
  BUSINESS_VALIDATION_TOAST_STYLE_CLASS,
} from '@interfaces/shared/notification-service.contants';
import { Message, MessageService } from 'primeng/api';

export { NotificationSeverity } from '@interfaces/shared/notification-service.contants';
export type { NotificationParams } from '@interfaces/shared/notification-service.contants';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements NotificationServiceInterface {
  private readonly _messageService = inject(MessageService);

  public create({
    severity,
    summary,
    detail,
    life = 3000,
    sticky = false,
    closable = true,
    icon,
    styleClass,
    ...others
  }: NotificationParams): void {
    const isError = severity === NotificationSeverity.error;

    const message: Message = {
      severity,
      summary,
      detail,
      life,
      closable,
      sticky,
      icon: icon ?? (isError ? ERROR_TOAST_DEFAULTS.icon : undefined),
      styleClass: styleClass ?? (isError ? ERROR_TOAST_STYLE_CLASS : undefined),
      ...others,
    };
    this._checkCustomNotificationValidity(message as NotificationParams);
    this._messageService.add(message);
  }

  public createMultiple(val: NotificationParams[]): void {
    val.forEach((item) => {
      this._checkCustomNotificationValidity(item);
    });

    const notifications: Message[] = val as Message[];
    this._messageService.addAll(notifications);
  }

  public removeAllNotifications(): void {
    this._messageService.clear();
  }

  public removeNotification(key: string): void {
    this._messageService.clear(key);
  }

  public createBusinessValidation({
    detail,
    summary = BUSINESS_VALIDATION_DEFAULTS.summary,
    life = BUSINESS_VALIDATION_DEFAULTS.life,
    sticky = false,
  }: Partial<
    Pick<NotificationParams, 'detail' | 'summary' | 'life' | 'sticky'>
  >): void {
    this.create({
      severity: NotificationSeverity.business,
      summary,
      detail,
      life,
      sticky,
      icon: BUSINESS_VALIDATION_DEFAULTS.icon,
      styleClass: BUSINESS_VALIDATION_TOAST_STYLE_CLASS,
    });
  }

  private _checkCustomNotificationValidity({
    severity,
    ...others
  }: NotificationParams) {
    if (severity === NotificationSeverity.custom) {
      if (!others.contentStyleClass)
        throw new Error('No contentStyleClass class provide');
    }
  }
}
